/**
 * Glyph icon SVG data URIs for orbital/canvas rendering.
 *
 * The orbital visualization and dial atoms render sphere glyphs onto a canvas /
 * as <img> sources, which cannot consume a React <GlyphAtom> or read CSS
 * variables. This module turns a unified glyph string into a standalone SVG
 * data URI using the offline-bundled Iconify collection (no CDN).
 */
import { getIcon, addCollection } from '@iconify/react';
import lucideData from '@iconify-json/lucide/icons.json';
import { isValidGlyphColor } from '@center-platform/spheres/glyph';
import type { GlyphColor } from '../types/glyph';
import { resolveGlyph } from '../components/atoms/Glyph/resolveGlyph';
import { normalizeLegacyGlyph } from '../components/atoms/Glyph/legacyGlyphMap';

// Register the Lucide collection at module load — offline, mirrors GlyphAtom.
addCollection(lucideData as never);

/** Neutral fallback used when a glyph is absent or unresolvable. */
export const DEFAULT_ICON_DATA_URI =
  "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='20'%20height='20'%20viewBox='0%200%20100%20100'%20fill='none'%3E%3Ccircle%20cx='50'%20cy='50'%20r='40'%20fill='none'%20stroke='%23CBD5E0'%20stroke-width='4'/%3E%3Ccircle%20cx='50'%20cy='50'%20r='10'%20fill='%23CBD5E0'/%3E%3C/svg%3E";

// Canvas data URIs cannot read CSS custom properties, so token colors resolve
// to a concrete neutral and hue colors resolve to HSL.
const NEUTRAL_ICON_COLOR = '#64748B';

function resolveGlyphColor(glyphColor?: GlyphColor): string {
  if (!isValidGlyphColor(glyphColor)) {
    return NEUTRAL_ICON_COLOR;
  }
  if (glyphColor.kind === 'hex') {
    return glyphColor.value;
  }
  if (glyphColor.kind === 'hue') {
    return `hsl(${glyphColor.value}, 60%, 50%)`;
  }
  // 'token' colors are CSS variables that can't be resolved inside a data URI,
  // so they fall back to a concrete neutral.
  return NEUTRAL_ICON_COLOR;
}

/**
 * Build a standalone SVG data URI for a glyph, for canvas/<img> rendering.
 * Falls back to {@link DEFAULT_ICON_DATA_URI} for empty, non-Iconify, or
 * unbundled glyphs (prime icons and letters have no canvas representation here).
 */
export function getGlyphIconDataUri(
  glyph: string | null | undefined,
  glyphColor?: GlyphColor
): string {
  // Normalize legacy schema-avatar CSS classes (e.g. 'center-organization-icon')
  // to Iconify names, mirroring GlyphAtom, so un-backfilled values still render.
  const normalizedGlyph = normalizeLegacyGlyph(glyph);
  if (resolveGlyph(normalizedGlyph) !== 'iconify') {
    return DEFAULT_ICON_DATA_URI;
  }

  const icon = getIcon(normalizedGlyph as string);
  if (!icon) {
    return DEFAULT_ICON_DATA_URI;
  }

  const width = icon.width ?? 24;
  const height = icon.height ?? 24;
  const color = resolveGlyphColor(glyphColor);
  const body = icon.body.replaceAll('currentColor', color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 ${width} ${height}">${body}</svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
